import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";
import { defineFieldConfigMap } from "@/components/shared/UniversalCRMTable/types/field-config-types";

/**
 * Saved view ids that include every deal field by default — kept in sync
 * with the same constant in `deal-fields-identity.ts`. `last_communication_at`
 * appears in every saved view.
 */
const ALL_DEAL_VIEW_IDS = [
  "all-deals",
  "sales-pipeline",
  "not-followed-up-this-week",
  "scheduled-followups",
  "in-progress",
  "closed-deals",
  "overdue-deals",
  "unassigned-deals",
  "at-risk",
  "won-deals",
  "deals-ready-to-invoice",
  "deals-invoiced",
  "recurring-revenue",
  "committed-revenue",
  "my-deals",
] as const;

export const timelineDealsFields = defineFieldConfigMap({
  // FIELD: created_at
  created_at: {
    type: "date",
    label: "Created",
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: false,
    // DB-managed (default now(), NOT NULL). Display-only — never sent on write.
    excludeFromApi: true,
    formSpan: 2,
    filterType: "dateRange",
    width: 130,
    minWidth: 110,
    maxWidth: 160,
    icon: "Plus",
    groupable: true,
    datetimePresets: {
      enabled: true,
      showCustomRange: true,
      presets: [
        "today",
        "tomorrow",
        "yesterday",
        "last7days",
        "last14days",
        "last30days",
        "last60days",
        "last90days",
        "thisweek",
        "lastweek",
        "nextweek",
        "thismonth",
        "lastmonth",
        "nextmonth",
        "thisquarter",
        "lastquarter",
        "nextquarter",
        "thisyear",
        "lastyear",
        "nextyear",
      ],
    },
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "timeline",
  },
  // FIELD: updated_at
  updated_at: {
    type: "date",
    label: "Updated",
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: false,
    // DB-managed (default now()). Display-only — never sent on write.
    excludeFromApi: true,
    formSpan: 2,
    filterType: "dateRange",
    width: 130,
    minWidth: 110,
    maxWidth: 160,
    icon: "Edit",
    groupable: true,
    datetimePresets: {
      enabled: true,
      showCustomRange: true,
      presets: [
        "today",
        "tomorrow",
        "yesterday",
        "last7days",
        "last14days",
        "last30days",
        "last60days",
        "last90days",
        "thisweek",
        "lastweek",
        "nextweek",
        "thismonth",
        "lastmonth",
        "nextmonth",
        "thisquarter",
        "lastquarter",
        "nextquarter",
        "thisyear",
        "lastyear",
        "nextyear",
      ],
    },
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "timeline",
  },
  // FIELD: last_communication_at
  last_communication_at: {
    type: "datetime",
    label: "Last activity",
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: false,
    isReadOnlyDerived: true,
    readOnlyReason: "Derived from the latest communication on any contact for this deal's company.",
    dataLineage: {
      kind: "trigger-populated",
      sourceTable: "communications",
      sourceField: "occurred_at",
      notes: "Per-company max(occurred_at) via DB trigger; see migration engagement_last_activity_from_communications.",
    },
    width: 168,
    minWidth: 120,
    maxWidth: 220,
    icon: "MessageSquare",
    groupable: true,
    groupingOptions: {
      dateGranularity: "day",
    },
    datetimePresets: {
      enabled: true,
      presets: ["today", "yesterday", "last7days", "last14days", "last30days", "thismonth", "lastmonth"],
      showCustomRange: true,
    },
    engagementStaleness: true,
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "timeline",
    savedViewMembership: ALL_DEAL_VIEW_IDS,
  },
  // FIELD: next_follow_up (synthesized from `followup-on-{YYYY-W##}` tag)
  next_follow_up: {
    type: "text",
    label: "Next Follow-Up",
    sortable: false,
    filterable: false,
    resizable: true,
    hideable: true,
    editable: false,
    isReadOnlyDerived: true,
    readOnlyReason:
      "Scheduled follow-up week, set via the Set follow-up date action. Stored as a deal tag.",
    excludeFromApi: true,
    dataLineage: {
      kind: "derived",
      sourceField: "tags",
      notes:
        "Derived from the deal's `followup-on-{YYYY-W##}` tag by getFieldValue → resolveNextFollowUp.",
    },
    width: 150,
    minWidth: 120,
    maxWidth: 200,
    icon: "CalendarClock",
    showOnCard: true,
    cardSpan: 1,
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "timeline",
    savedViewMembership: ALL_DEAL_VIEW_IDS,
  },
  // FIELD: won_at
  won_at: {
    type: "date",
    label: "Won Date",
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: true,
    formSpan: 1,
    filterType: "dateRange",
    width: 130,
    minWidth: 110,
    maxWidth: 160,
    icon: "Trophy",
    groupable: true,
    datetimePresets: {
      enabled: true,
      showCustomRange: true,
      presets: [
        "today",
        "tomorrow",
        "yesterday",
        "last7days",
        "last14days",
        "last30days",
        "last60days",
        "last90days",
        "thisweek",
        "lastweek",
        "nextweek",
        "thismonth",
        "lastmonth",
        "nextmonth",
        "thisquarter",
        "lastquarter",
        "nextquarter",
        "thisyear",
        "lastyear",
        "nextyear",
      ],
    },
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "timeline",
    savedViewMembership: ["closed-deals", "won-deals"],
  },
  // FIELD: lost_at
  lost_at: {
    type: "date",
    label: "Lost Date",
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: true,
    formSpan: 1,
    filterType: "dateRange",
    width: 130,
    minWidth: 110,
    maxWidth: 160,
    icon: "X",
    groupable: true,
    datetimePresets: {
      enabled: true,
      showCustomRange: true,
      presets: [
        "today",
        "tomorrow",
        "yesterday",
        "last7days",
        "last14days",
        "last30days",
        "last60days",
        "last90days",
        "thisweek",
        "lastweek",
        "nextweek",
        "thismonth",
        "lastmonth",
        "nextmonth",
        "thisquarter",
        "lastquarter",
        "nextquarter",
        "thisyear",
        "lastyear",
        "nextyear",
      ],
    },
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "timeline",
  },
});
